import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { IModalProps, ModalSize } from './types'
import { targetIsDatepicker } from '@sharedComponents/InputDatePicker/utils'
import { addClass, removeClass } from '@frontendJs/util/helpers'
import { targetIsScrollbar } from './utils'

const ModalPortal = (props: IModalProps) => {
    const modalDialogRef = useRef<HTMLDivElement>(null)
    const previousActiveElement = useRef<HTMLElement | null>(null);

    const { t } = useTranslation()
    const OPEN_CSS_CLASS = 'modal--show'

    function HandleClose() {
        if (props.handleClose) {
            props.handleClose()
            previousActiveElement.current?.focus();
        }

        if (props.onAfterClose) {
            props.onAfterClose()
        }
    }

    const isMultipleModalsOpen = () => {
        if (document) {
            const allModals = document.querySelectorAll('.modal--show')
            const nrOfOpenModals = allModals && allModals.length
            return !!(nrOfOpenModals > 1)
        }
        return false
    }

    function HandleClickOutside(event: any) {
        if (
            targetIsScrollbar(event) ||
            targetIsDatepicker(event.target) ||
            isMultipleModalsOpen()
        ) {
            return
        }
        if (
            modalDialogRef.current &&
            !modalDialogRef.current.contains(event.target)
        ) {
            HandleClose()
        }
    }

    function isFocusable(el: HTMLElement) {
        if ((el as any).disabled) return false;

        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;

        if (!el.offsetParent && style.position !== 'fixed') return false;
        return typeof el.focus === 'function';
    }

    function handleKeyboardNavigation(event: KeyboardEvent) {
        if (!props.open) return;
        if (event.key === 'Escape') {
            HandleClose();
            return;
        }
        if (event.key === 'Tab') {
            const elements = modalDialogRef.current?.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
            const focusableElements = Array.from(elements ?? []).filter(isFocusable);

            if (!focusableElements || focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const isShift = event.shiftKey;
            const activeElement = document.activeElement as HTMLElement;

            if (isShift) {
                // SHIFT + TAB
                if (activeElement === firstElement) {
                    lastElement.focus();
                    event.preventDefault();
                }

                if (activeElement === modalDialogRef.current) {
                    lastElement.focus();
                    event.preventDefault();
                }
            } else {
                // TAB forward
                if (activeElement === lastElement) {
                    firstElement.focus();
                    event.preventDefault();
                }

                if (activeElement === modalDialogRef.current) {
                    firstElement.focus();
                    event.preventDefault();
                }
            }
        }
    }


    useEffect(() => {
        if (!props.open) return

        // Events
        if (props.shouldCloseOnOverlayClick) {
            document.addEventListener('mouseup', HandleClickOutside)
        }

        document.addEventListener('keydown', handleKeyboardNavigation)

        return () => {
            if (!props.open) return

            if (props.shouldCloseOnOverlayClick) {
                document.removeEventListener('mouseup', HandleClickOutside)
            }

            document.removeEventListener('keydown', handleKeyboardNavigation)
        }
    }, [props, props.open]);

    useEffect(() => {
        if (props.open) {
            previousActiveElement.current = document.activeElement as HTMLElement;
            addClass(document.body, 'disable-scroll')
            modalDialogRef.current?.focus()
        } else {
            removeClass(document.body, 'disable-scroll')
            previousActiveElement.current?.focus()
        }
    }, [props.open]);

    useEffect(() => {
      return () => {
        document.body.classList.remove('disable-scroll');
      };
    }, []);

  function RenderModal() {
        return (
            <div
                className={`modal ${props.className || ''} ${
                    props.open ? OPEN_CSS_CLASS : ''
                }`}
                aria-hidden={!props.open}
            >
                <div className={`modal__dialog --${props.size || ModalSize.Large}`}
                    role={props.role}
                    aria-modal={props.role === 'dialog' ? 'true' : 'false'}
                    ref={modalDialogRef}
                    tabIndex={-1}
                >
                    <div className={`modal__content ${props.noPadding? 'modal__content--noPadding' : ''}`}>
                        {props.children}
                        <div className="modal__close-button-wrapper" hidden={props.disableCloseButton}>
                            <button
                                className='modal__close-button'
                                hidden={props.disableCloseButton}
                                onClick={HandleClose}
                                aria-label={t('modal.closeModal')}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (typeof document === 'undefined') {
        return <></>
    }

    return ReactDOM.createPortal(RenderModal(), props.domNode || document.body)
}

ModalPortal.defaultProps = {
    shouldCloseOnEsc: true,
    shouldCloseOnOverlayClick: true,
    role: 'dialog',
}

export default ModalPortal